/* eslint-disable class-methods-use-this */
import { del as apiDelete, get, patch, post, put } from 'aws-amplify/api'

import type { PostOperation } from '@aws-amplify/api-rest/dist/esm/types'

import { API_NAME } from '~/constants'
import HttpServiceBase, {
  HttpQueryValue,
  HttpRequestBase,
  HttpServiceBaseResponse,
} from '~/services/http/models'

function buildQueryString(query: Record<string, HttpQueryValue>): string {
  const searchParams = new URLSearchParams()
  Object.entries(query).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, v))
    } else {
      searchParams.append(key, value)
    }
  })
  return searchParams.toString()
}

class AmplifyHttpService implements HttpServiceBase {
  public async get<T>({ path, query, headers }: HttpRequestBase) {
    const pathWithQuery =
      query && Object.keys(query).length > 0
        ? `${path}?${buildQueryString(query)}`
        : path
    const operation = get({
      apiName: API_NAME,
      path: pathWithQuery,
      ...(headers ? { options: { headers } } : {}),
    })

    const { body } = await operation.response
    return body.json() as Promise<HttpServiceBaseResponse<T>>
  }

  public async post<T>({ path, query, payload, headers }: HttpRequestBase) {
    const pathWithQuery =
      query && Object.keys(query).length > 0
        ? `${path}?${buildQueryString(query)}`
        : path
    const operation = post({
      apiName: API_NAME,
      path: pathWithQuery,
      options: {
        body: payload as FormData,
        ...(headers ? { headers } : {}),
      },
    })
    const { body } = await operation.response
    return body.json() as Promise<HttpServiceBaseResponse<T>>
  }

  public async put<T>({ path, query, payload, headers }: HttpRequestBase) {
    const pathWithQuery =
      query && Object.keys(query).length > 0
        ? `${path}?${buildQueryString(query)}`
        : path
    const operation = put({
      apiName: API_NAME,
      path: pathWithQuery,
      options: {
        body: payload as FormData,
        ...(headers ? { headers } : {}),
      },
    })

    const { body } = await operation.response
    return body.json() as Promise<HttpServiceBaseResponse<T>>
  }

  public async patch<T>({ path, query, payload, headers }: HttpRequestBase) {
    const pathWithQuery =
      query && Object.keys(query).length > 0
        ? `${path}?${buildQueryString(query)}`
        : path
    const operation = patch({
      apiName: API_NAME,
      path: pathWithQuery,
      options: {
        body: payload as FormData,
        ...(headers ? { headers } : {}),
      },
    })

    const { body } = await operation.response
    return body.json() as Promise<HttpServiceBaseResponse<T>>
  }

  public async delete<T>({ path, query, headers }: HttpRequestBase) {
    const pathWithQuery =
      query && Object.keys(query).length > 0
        ? `${path}?${buildQueryString(query)}`
        : path
    const operation = apiDelete({
      apiName: API_NAME,
      path: pathWithQuery,
      ...(headers ? { options: { headers } } : {}),
    }) as PostOperation

    const { body } = await operation.response
    return body.json() as Promise<HttpServiceBaseResponse<T>>
  }
}

export default AmplifyHttpService
